/**
 * @file SearchResultCard.tsx
 * @description Enhanced horizontal card for search results with match context
 *
 * Shows why a result matched:
 * - Title match: Standard display
 * - Director match: Shows director name badge
 * - Cast match: Shows cast member names
 * - Synopsis match: Shows matched text snippet
 * - Fuzzy match: Shows "Did you mean..." suggestion
 */

import type { ReactElement } from "react";
import type { ApiTitle } from "@lumi/data";
import { Button } from "@lumi/ui-headless";
import type { Variants } from "framer-motion";
import { Bookmark, Globe, Info, Languages, Play, User, Users } from "lucide-react";
import { NewBadge } from "../../../shared/components/NewBadge";
import { useCascadeFadeIn, useWatchlist } from "../../../shared/hooks";
import { pageTransitions } from "../../../shared/transitions/pageTransitions";
import type { SearchResult } from "../hooks/useSearch";

interface SearchResultCardProps {
  readonly result: SearchResult;
  readonly title: ApiTitle;
  readonly index?: number;
  readonly transitionTo: (path: string, variant?: Variants) => void;
  readonly onContentClick?: (action: string, contentId: string, metadata?: Record<string, unknown>) => void;
}

/**
 * Match type badge component
 */
function MatchTypeBadge({
  matchType,
  matchContext,
}: {
  readonly matchType: SearchResult["matchType"];
  readonly matchContext: string | null;
}) {
  const badgeConfig: Record<
    SearchResult["matchType"],
    { icon: ReactElement | null; label: string | null; color: string | null }
  > = {
    title: { icon: null, label: null, color: null },
    director: { icon: <User size={12} />, label: "Director", color: "bg-blue-500/20 text-blue-300" },
    cast: { icon: <Users size={12} />, label: "Cast", color: "bg-purple-500/20 text-purple-300" },
    synopsis: { icon: null, label: "Synopsis", color: "bg-green-500/20 text-green-300" },
    country: { icon: <Globe size={12} />, label: "Country", color: "bg-orange-500/20 text-orange-300" },
    language: { icon: <Languages size={12} />, label: "Language", color: "bg-cyan-500/20 text-cyan-300" },
    fuzzy: { icon: null, label: "Similar", color: "bg-amber-500/20 text-amber-300" },
  };

  const config = badgeConfig[matchType];

  if (matchType === "title" || !config.label) return null;

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      {config.icon}
      <span>{config.label}</span>
      {matchContext && matchType !== "synopsis" && <span className="text-white/70">: {matchContext}</span>}
    </div>
  );
} /**
 * Match context snippet component
 */
function MatchContext({
  matchType,
  matchContext,
}: {
  readonly matchType: SearchResult["matchType"];
  readonly matchContext: string | null;
}) {
  if (!matchContext || matchType !== "synopsis") return null;

  return <p className="text-xs text-neutral-400 italic line-clamp-2">"...{matchContext}..."</p>;
}

/**
 * Card image with play overlay
 */
function CardImage({ title, onPlay }: { readonly title: ApiTitle; readonly onPlay: () => void }) {
  return (
    <div
      className="
            relative aspect-video overflow-hidden rounded-xl
            outline outline-transparent
            bg-neutral-900 ring-1 ring-neutral-800/50
            shadow-[0_10px_40px_rgba(0,0,0,1)]
            transition-all delay-100 duration-300 ease-in-out
            hover:scale-[0.93]
            hover:outline-offset-8
            hover:outline-lumi-accent
            hover:outline-[2px]
        "
    >
      {title.createdAt ? <NewBadge createdAt={title.createdAt} /> : null}

      <img
        src={title.horizontalTileUrl ?? title.backdropUrlHd ?? title.posterUrlHd ?? title.posterUrl ?? undefined}
        alt={title.name}
        className="h-full w-full object-cover"
      />

      <div
        className="
                absolute inset-0
                bg-gradient-to-t from-black/80
                via-black/40 to-transparent
                opacity-0
                group-hover:opacity-100
                transition-opacity
                delay-100 duration-300
                ease-in-out z-10
            "
      />

      <div
        className="
                absolute inset-0 flex items-center justify-center
                opacity-0 group-hover:opacity-100
                transition-opacity delay-100 duration-300 ease-in-out z-10"
      >
        <Button variant="ghost-sm" onClick={onPlay}>
          <Play size={20} />
        </Button>
      </div>
    </div>
  );
}

export function SearchResultCard({
  result,
  title,
  index = 0,
  transitionTo,
  onContentClick,
}: SearchResultCardProps): ReactElement {
  const { inWatchlist, toggle, mutating } = useWatchlist(title.id);
  const { ref, isVisible } = useCascadeFadeIn({ index });

  const handlePlay = () => {
    onContentClick?.("search_result_play", title.id, {
      matchType: result.matchType,
      query: result.matchContext,
      relevance: result.relevance,
    });
    transitionTo(`/home/watch/${title.slug}`, pageTransitions.cinematicFade);
  };

  const handleMoreInfo = () => {
    onContentClick?.("search_result_info", title.id, {
      matchType: result.matchType,
      query: result.matchContext,
      relevance: result.relevance,
    });
    transitionTo(`/home/title/${title.slug}`, pageTransitions.fullScreenFade);
  };

  const handleWatchlistToggle = () => {
    void toggle();
  };

  return (
    <div
      ref={ref}
      className={`
                group flex-shrink-0 self-start text-left w-[calc(100vw-4rem)] sm:w-[500px] space-y-3
                transition-all duration-500 ease-out
                ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
            `}
    >
      <CardImage title={title} onPlay={handlePlay} />

      {/* Metadata section */}
      <div className="space-y-2">
        {/* Match type badge */}
        <MatchTypeBadge matchType={result.matchType} matchContext={result.matchContext} />

        {/* Title and metadata */}
        <div className="px-1 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-2">
              <h2 className="font-medium text-white text-base leading-tight line-clamp-2">{title.name}</h2>

              <div className="space-y-1">
                {/* Year, Runtime, and Genres */}
                <div className="flex items-center gap-2 text-xs text-neutral-400 min-w-0">
                  {title.year && <span className="flex-shrink-0">{title.year}</span>}
                  {title.year && title.runtimeMinutes && <span className="flex-shrink-0">•</span>}
                  {title.runtimeMinutes && <span className="flex-shrink-0">{title.runtimeMinutes} min</span>}
                  {(title.year || title.runtimeMinutes) && title.genres && title.genres.length > 0 && (
                    <span className="flex-shrink-0">•</span>
                  )}
                  {title.genres && title.genres.length > 0 && (
                    <span className="truncate">{title.genres.slice(0, 2).join(", ")}</span>
                  )}
                </div>

                {/* Director */}
                {title.directorName && (
                  <p className="text-xs text-neutral-500 truncate">
                    Dir. <span className="text-neutral-400 font-medium">{title.directorName}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Action buttons - aligned to the right at title level */}
            <div className="flex items-center gap-2 pointer-events-none">
              <Button
                variant="ghost-sm"
                className="pl-0 pb-6 flex-shrink-0 pointer-events-auto"
                onClick={handleWatchlistToggle}
                disabled={mutating}
                aria-label={inWatchlist ? "Remove from watchlist" : "Add to watchlist"}
              >
                <Bookmark
                  size={16}
                  fill={inWatchlist ? "currentColor" : "none"}
                  className={inWatchlist ? "text-lumi-accent" : ""}
                />
              </Button>
              <Button variant="ghost-sm" className="pl-0 pb-6 pointer-events-auto" onClick={handleMoreInfo}>
                <Info size={16} />
              </Button>
            </div>
          </div>

          {/* Match context for synopsis matches */}
          <MatchContext matchType={result.matchType} matchContext={result.matchContext} />
        </div>
      </div>
    </div>
  );
}
